import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Button, Card, Checkbox, Col, DatePicker, Modal, Row,
  Progress, Space, Statistic, Table, Tag, Typography, message
} from 'antd';
import {
  CheckCircleOutlined, EyeOutlined, FilePdfOutlined,
  PlayCircleOutlined, StopOutlined, WarningOutlined, SyncOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../utils/api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const statusMeta = {
  ready: ['พร้อมลงบัญชี', 'processing'],
  posted: ['สำเร็จ', 'success'],
  skipped: ['ข้าม', 'default'],
  reversed: ['กลับรายการแล้ว', 'warning'],
  reposted: ['กลับรายการและลงใหม่แล้ว', 'success'],
  failed: ['ผิดพลาด', 'error']
};
const documentLabel = type => ({ receipt:'รายได้',expense:'ค่าใช้จ่าย',inventory:'สต็อกสินค้า',investor:'ส่วนแบ่งนักลงทุน' }[type] || type);
const businessLabel = line => ({ software:'ซอฟต์แวร์',sign_printing:'ร้านป้าย',shared:'ค่าใช้จ่ายร่วม' }[line] || 'ยังไม่ระบุ');

const BulkPostingPage = () => {
  const [period, setPeriod] = useState([dayjs().startOf('month'), dayjs()]);
  const [documentTypes, setDocumentTypes] = useState(['receipt', 'expense', 'inventory']);
  const [report, setReport] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [posting, setPosting] = useState(false);
  const [operation, setOperation] = useState('posting');
  const [jobProgress, setJobProgress] = useState(null);
  const pollActive = useRef(true);
  useEffect(() => () => { pollActive.current = false; }, []);

  const pollPostingProgress = async runId => {
    try {
      const response = await api.get(`/api/accounting/bulk-posting/progress/${runId}`);
      const progress = response.data.data;
      if (!pollActive.current) return;
      setJobProgress(progress); setReport(progress);
      if (progress.status === 'completed') {
        setPosting(false); message.success(`ลงบัญชีสำเร็จ ${progress.posted_count} เอกสาร`); return;
      }
      if (progress.status === 'failed') {
        setPosting(false); message.error(progress.error_message || 'งานลงบัญชีล้มเหลว'); return;
      }
      setTimeout(() => pollPostingProgress(runId), 700);
    } catch (error) {
      if (!pollActive.current) return;
      setPosting(false); message.error(error.response?.data?.message || 'ติดตามความคืบหน้าไม่สำเร็จ');
    }
  };

  const params = () => ({
    start_date: period[0].format('YYYY-MM-DD'),
    end_date: period[1].format('YYYY-MM-DD'),
    document_types: documentTypes.join(',')
  });
  const reversalParams = () => ({ ...params(),
    document_types:documentTypes.filter(type=>['receipt','expense'].includes(type)).join(',') });

  const preview = async () => {
    if (!documentTypes.length) return message.warning('กรุณาเลือกประเภทเอกสาร');
    setPreviewing(true);
    try {
      const response = await api.get('/api/accounting/bulk-posting/preview', { params: params() });
      setOperation('posting');
      setReport(response.data.data);
    } catch (error) {
      message.error(error.response?.data?.message || 'ตรวจสอบรายการไม่สำเร็จ');
    } finally {
      setPreviewing(false);
    }
  };

  const previewReversal = async () => {
    if (!documentTypes.some(type=>['receipt','expense'].includes(type))) return message.warning('เลือกรายได้หรือค่าใช้จ่ายเพื่อกลับรายการ');
    setPreviewing(true);
    try {
      const response = await api.get('/api/accounting/bulk-posting/reversal-preview', { params: reversalParams() });
      setOperation('reversal');
      setReport(response.data.data);
      if (!response.data.data.total_documents) message.info('ไม่พบรายการลงบัญชีในช่วงเวลานี้');
    } catch (error) {
      message.error(error.response?.data?.message || 'ตรวจสอบรายการเดิมไม่สำเร็จ');
    } finally {
      setPreviewing(false);
    }
  };

  const executeReverseAndRepost = () => {
    Modal.confirm({
      title: 'ยืนยันกลับรายการและลงบัญชีใหม่',
      width: 620,
      content: (
        <Alert
          type="warning"
          showIcon
          message="ระบบจะไม่ลบประวัติสมุดรายวัน"
          description={`ระบบจะสร้าง JV กลับรายการสำหรับเอกสาร ${report?.total_documents || 0} รายการ แล้วลงบัญชีใหม่จากข้อมูลเอกสารปัจจุบัน ระหว่าง ${period[0].format('DD/MM/YYYY')} ถึง ${period[1].format('DD/MM/YYYY')}`}
        />
      ),
      okText: 'ยืนยัน Reverse & Repost',
      okButtonProps: { danger: true },
      cancelText: 'ยกเลิก',
      onOk: async () => {
        setPosting(true);
        try {
          const response = await api.post('/api/accounting/bulk-posting/reverse', { ...reversalParams(), repost: true });
          setOperation('reversal-result');
          setReport(response.data.data);
          message.success(`กลับรายการและลงใหม่สำเร็จ ${response.data.data.reposted_count} เอกสาร`);
        } catch (error) {
          message.error(error.response?.data?.message || 'กลับรายการและลงบัญชีใหม่ไม่สำเร็จ');
        } finally {
          setPosting(false);
        }
      }
    });
  };

  const executePosting = () => {
    Modal.confirm({
      title: 'ยืนยันลงบัญชีแบบ One Click',
      content: `ระบบจะลงบัญชีเอกสารที่พร้อมระหว่าง ${period[0].format('DD/MM/YYYY')} ถึง ${period[1].format('DD/MM/YYYY')} และข้ามเอกสารที่เคยลงแล้ว`,
      okText: 'ยืนยันลงบัญชี',
      cancelText: 'ยกเลิก',
      onOk: async () => {
        setPosting(true);
        setJobProgress({ status: 'queued', processed_count: 0, total_documents: report?.ready_count || report?.total_documents || 0, progress_percent: 0 });
        try {
          const response = await api.post('/api/accounting/bulk-posting/post', params());
          pollActive.current = true;
          await pollPostingProgress(response.data.data.run_id);
        } catch (error) {
          message.error(error.response?.data?.message || 'ลงบัญชีไม่สำเร็จ');
          setPosting(false);
        }
      }
    });
  };

  const exportPdf = () => {
    if (!report?.results?.length) return message.warning('ไม่มีผลรายการสำหรับส่งออก');
    if (!window.pdfMake) return message.error('PDF engine is not available');
    window.pdfMake.createPdf({
      defaultStyle: { font: 'THSarabunNew', fontSize: 14 },
      content: [
        { text: 'รายงานผลการลงบัญชีแบบ One Click', style: 'header', alignment: 'center' },
        { text: `เอกสารวันที่ ${period[0].format('DD/MM/YYYY')} ถึง ${period[1].format('DD/MM/YYYY')}`, alignment: 'center', margin: [0, 0, 0, 12] },
        { text: `ทั้งหมด ${report.total_documents || 0} | สำเร็จ ${report.posted_count || 0} | ข้าม ${report.skipped_count || 0} | ผิดพลาด ${report.failed_count || 0}`, margin: [0, 0, 0, 10] },
        { table: { headerRows: 1, widths: ['auto', '*', 'auto', 'auto', '*'], body: [
          ['ประเภท', 'เลขที่เอกสาร', 'วันที่', 'ผลลัพธ์', 'JV/หมายเหตุ'],
          ...report.results.map(item => [
            documentLabel(item.document_type),
            item.document_number || '', dayjs(item.document_date).format('DD/MM/YYYY'),
            statusMeta[item.result_status]?.[0] || item.result_status,
            item.reposted_jv_number || item.reversal_jv_number || item.jv_number || item.message || ''
          ])
        ] } }
      ],
      styles: { header: { fontSize: 22, bold: true } }
    }).download(`bulk-posting-${period[0].format('YYYYMMDD')}-${period[1].format('YYYYMMDD')}.pdf`);
  };

  const results = report?.results || [];
  const columns = [
    { title: 'ประเภท', dataIndex: 'document_type', width: 145, render: value => <Tag color={value === 'receipt' ? 'green' : value === 'expense' ? 'red' : value === 'investor' ? 'purple' : 'blue'}>{documentLabel(value)}</Tag> },
    { title: 'เลขที่เอกสาร', dataIndex: 'document_number', width: 170 },
    { title: 'วันที่', dataIndex: 'document_date', width: 110, render: value => dayjs(value).format('DD/MM/YYYY') },
    { title: 'ธุรกิจ / ผังบัญชี', width: 200, render: (_,row) => <Space direction="vertical" size={2}>
      {row.business_line && <Tag color="cyan">{businessLabel(row.business_line)}</Tag>}
      {row.document_type !== 'investor' && (row.preset_name ? <Tag color="blue">{row.preset_name}</Tag> : <Tag>ค่ากลางระบบ</Tag>)}
    </Space> },
    { title: 'ผลตรวจสอบ', dataIndex: 'result_status', width: 130, render: value => <Tag color={statusMeta[value]?.[1]}>{statusMeta[value]?.[0] || value}</Tag> },
    { title: 'เลขที่ JV', key: 'jv_number', width: 170, render: (_, row) => row.reposted_jv_number || row.reversal_jv_number || row.jv_number || '-' },
    { title: 'รายละเอียด', render: (_,row) => <Space direction="vertical" size={2}>
      <span>{row.message || '-'}</span>
      {row.investor_warning && <Text type="warning">{row.investor_warning}</Text>}
    </Space> }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>ลงบัญชีอัตโนมัติ One Click</Title>
      <Text type="secondary">ประมวลผลเอกสารและ Draft Journal ที่ตรวจแล้วตามช่วงเวลา โดยใช้ผังบัญชีที่กำหนดไว้</Text>

      <Card bordered={false} style={{ marginTop: 18, borderRadius: 14 }}>
        <Space wrap size="large">
          <RangePicker value={period} onChange={value => {if(value){setPeriod(value);setReport(null);setOperation('posting')}}} allowClear={false} />
          <Checkbox.Group value={documentTypes} onChange={value => {setDocumentTypes(value);setReport(null);setOperation('posting')}} options={[
            { label: 'รายได้ / ใบเสร็จ', value: 'receipt' },
            { label: 'ค่าใช้จ่าย', value: 'expense' },
            { label: 'Draft Journal สต็อก', value: 'inventory' },
            { label: 'Draft Journal นักลงทุน', value: 'investor' }
          ]} />
          <Button icon={<EyeOutlined />} loading={previewing} onClick={preview}>ตรวจสอบก่อนลงบัญชี</Button>
          <Button type="primary" icon={<PlayCircleOutlined />} loading={posting} disabled={!report?.ready_count && !results.some(item => item.result_status === 'ready')} onClick={executePosting}>
            ลงบัญชี One Click
          </Button>
          <Button icon={<SyncOutlined />} loading={previewing} disabled={!documentTypes.some(type=>['receipt','expense'].includes(type))} onClick={previewReversal}>
            ตรวจรายการที่ลงแล้ว
          </Button>
          <Button
            danger
            icon={<SyncOutlined spin={posting} />}
            loading={posting}
            disabled={operation !== 'reversal' || !report?.ready_count}
            onClick={executeReverseAndRepost}
          >
            กลับรายการและลงใหม่
          </Button>
          <Button danger icon={<FilePdfOutlined />} disabled={!results.length} onClick={exportPdf}>รายงาน PDF</Button>
        </Space>
      </Card>

      <Alert style={{marginTop:12}} type="info" showIcon
        message="ส่วนแบ่งนักลงทุนลงบัญชีหลังปิดงวดและตรวจ GL"
        description="เลือกช่วงวันที่ให้ครอบคลุมวันสิ้นงวดของส่วนแบ่ง หรือวันที่ทำจ่าย ใบเสร็จและค่าใช้จ่ายลงบัญชีตามปกติแม้ยังไม่จัดประเภทธุรกิจ แต่ต้องจัดประเภทให้ครบก่อนปิดงวดนักลงทุน การกลับรายการใช้ได้เฉพาะใบเสร็จและค่าใช้จ่าย" />

      {posting && jobProgress && (
        <Card bordered={false} style={{ marginTop: 16, borderRadius: 14 }}>
          <Progress percent={Number(jobProgress.progress_percent || 0)} status="active" strokeColor={{ '0%': '#1677ff', '100%': '#52c41a' }} />
          <Row gutter={[12, 8]}>
            <Col xs={24} md={8}><Text strong>ดำเนินการแล้ว: {jobProgress.processed_count || 0} / {jobProgress.total_documents || 0} รายการ</Text></Col>
            <Col xs={24} md={8}><Text>เอกสารปัจจุบัน: {jobProgress.current_document_number || 'กำลังเตรียมงาน'}</Text></Col>
            <Col xs={24} md={8}><Text type="secondary">สำเร็จ {jobProgress.posted_count || 0} · ข้าม {jobProgress.skipped_count || 0} · ผิดพลาด {jobProgress.failed_count || 0}</Text></Col>
          </Row>
        </Card>
      )}

      {report && <>
        <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
          <Col xs={12} md={6}><Card><Statistic title="เอกสารทั้งหมด" value={report.total_documents || 0} /></Card></Col>
          <Col xs={12} md={6}><Card><Statistic title={operation.startsWith('reversal') ? 'กลับรายการ/พร้อม' : 'สำเร็จ/พร้อม'} value={report.reposted_count ?? report.reversed_count ?? report.posted_count ?? report.ready_count ?? 0} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#389e0d' }} /></Card></Col>
          <Col xs={12} md={6}><Card><Statistic title="ข้าม" value={report.skipped_count || 0} prefix={<StopOutlined />} /></Card></Col>
          <Col xs={12} md={6}><Card><Statistic title="ผิดพลาด" value={report.failed_count || 0} prefix={<WarningOutlined />} valueStyle={{ color: '#cf1322' }} /></Card></Col>
        </Row>
        {report.failed_count > 0 && <Alert style={{ marginTop: 16 }} type="warning" showIcon message="มีเอกสารที่ลงบัญชีไม่ได้" description="ตรวจสอบรายละเอียดในตารางและแก้การตั้งค่าผังบัญชีหรือสถานะเอกสารก่อนประมวลผลอีกครั้ง" />}
        {results.some(item=>item.investor_warning) && <Alert style={{marginTop:12}} type="warning" showIcon
          message={`${results.filter(item=>item.investor_warning).length} เอกสารต้องจัดประเภทสำหรับงวดนักลงทุน`}
          description="การลงบัญชีรายการเหล่านี้ไม่ถูกขวาง แต่ยังปิดงวดส่วนแบ่งนักลงทุนไม่ได้จนกว่าจะแก้ข้อมูลครบ" />}
        <Card bordered={false} style={{ marginTop: 16, borderRadius: 14 }}>
          <Table rowKey={item => `${item.document_type}-${item.id}`} columns={columns} dataSource={results} scroll={{ x: 900 }} pagination={{ pageSize: 20 }} />
        </Card>
      </>}
    </div>
  );
};

export default BulkPostingPage;
